import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
// Autocomplete is still used for Bonus Type + Specification pickers.

import CustomerPicker from './CustomerPicker';
import ResponsiveSheet, { useSheetFullScreen } from './ResponsiveSheet';
import SheetHeader from './SheetHeader';
import { TOKENS } from '../theme';

// Spec §4 — Bonus edit/add sheet.
//
// Both EDIT and ADD modes mirror the employee Bonus.jsx flow:
//   - Customer picker (Autocomplete bound to customers list)
//   - Bonus type picker (Autocomplete bound to ManageBonus configs)
//   - Specification picker (filtered by selected bonus)
//   - Machine + amount + time fields
//
// ADD mode: blank pickers, manager picks fresh.
// EDIT mode: pickers pre-fill from the entry's customerId / manageBonusId.
//
// Photos are always skipped from the form. In edit mode the existing
// photo is preserved as-is. In add mode none is captured (manager not
// present at the counter). The audit log marks adds as "Late entry".
export default function BonusEditSheet({
  open,
  onClose,
  onSave,
  mode = 'edit',
  entry = {},
  index,
  customers = [],
  manageBonusList = [],
}) {
  const isAdd = mode === 'add';

  // Resolve pre-fill values from the existing entry's foreign keys.
  const initialCustomer = useMemo(
    () => customers?.find((c) => c.customerId === entry.customerId) || null,
    [customers, entry.customerId]
  );
  const initialManageBonus = useMemo(
    () =>
      manageBonusList?.find((b) => b.manageBonusId === entry.manageBonusId) ||
      null,
    [manageBonusList, entry.manageBonusId]
  );
  const initialSpec = useMemo(() => {
    if (!initialManageBonus) return null;
    return (
      initialManageBonus.specifications?.find(
        (s) =>
          s.description === entry.specify ||
          String(s.amount) === String(entry.bonusAmount)
      ) || null
    );
  }, [initialManageBonus, entry.specify, entry.bonusAmount]);

  const [selectedCustomer, setSelectedCustomer] = useState(initialCustomer);
  const [selectedManageBonus, setSelectedManageBonus] =
    useState(initialManageBonus);
  const [selectedSpec, setSelectedSpec] = useState(initialSpec);
  const [machine, setMachine] = useState(entry.machine || '');
  const [amount, setAmount] = useState(entry.bonusAmount || entry.amount || '');
  const [time, setTime] = useState(entry.time || '');

  // Re-sync local state when the lookups arrive after the sheet opens.
  useEffect(() => {
    if (initialCustomer) setSelectedCustomer(initialCustomer);
  }, [initialCustomer]);
  useEffect(() => {
    if (initialManageBonus) setSelectedManageBonus(initialManageBonus);
  }, [initialManageBonus]);
  useEffect(() => {
    if (initialSpec) setSelectedSpec(initialSpec);
  }, [initialSpec]);

  const specOptions = useMemo(
    () => selectedManageBonus?.specifications || [],
    [selectedManageBonus]
  );

  useEffect(() => {
    if (selectedSpec?.amount != null) setAmount(String(selectedSpec.amount));
  }, [selectedSpec]);

  const canSave = Boolean(
    selectedCustomer && selectedManageBonus && selectedSpec && machine
  );

  const handleSave = () => {
    if (!canSave) return;
    const payload = {
      customerId: selectedCustomer.customerId,
      manageBonusId: selectedManageBonus.manageBonusId,
      specification: {
        ...selectedSpec,
        amount: String(amount || selectedSpec.amount),
      },
      machine: String(machine),
      time: time || undefined,
      bonusAmount: String(amount),
    };
    onSave(payload);
  };

  const isPage = useSheetFullScreen();

  return (
    <ResponsiveSheet open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: isPage ? '100%' : undefined,
          maxHeight: isPage ? '100%' : 'inherit',
        }}
      >
        <SheetHeader
          eyebrow="Bonus"
          eyebrowColor={TOKENS.amber700}
          title={isAdd ? 'Add Bonus' : `Edit Bonus #${index ?? '—'}`}
          onClose={onClose}
        />
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <CustomerPicker
            value={selectedCustomer}
            onChange={setSelectedCustomer}
            customers={customers}
          />
          <Field label="Bonus Type*">
            <Autocomplete
              options={manageBonusList || []}
              getOptionLabel={(b) => b?.name || ''}
              isOptionEqualToValue={(a, b) =>
                a.manageBonusId === b.manageBonusId
              }
              value={selectedManageBonus}
              onChange={(_e, v) => {
                setSelectedManageBonus(v);
                setSelectedSpec(null);
              }}
              renderInput={(p) => (
                <TextField {...p} size="small" placeholder="Pick bonus..." />
              )}
            />
          </Field>
          <Field label="Specification*">
            <Autocomplete
              options={specOptions}
              getOptionLabel={(s) =>
                s ? `${s.description} · $${s.amount}` : ''
              }
              isOptionEqualToValue={(a, b) =>
                a.description === b.description &&
                String(a.amount) === String(b.amount)
              }
              value={selectedSpec}
              onChange={(_e, v) => setSelectedSpec(v)}
              disabled={!selectedManageBonus}
              renderInput={(p) => (
                <TextField
                  {...p}
                  size="small"
                  placeholder={
                    selectedManageBonus
                      ? 'Pick a spec...'
                      : 'Pick a bonus first'
                  }
                />
              )}
            />
          </Field>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Field label="Machine*">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={machine}
                  onChange={(e) => setMachine(e.target.value)}
                />
              </Field>
            </Grid>
            <Grid item xs={6}>
              <Field label="Amount $">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Field>
            </Grid>
          </Grid>
          {!isAdd && (
            <Field label="Time">
              <TextField
                fullWidth
                size="small"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                placeholder="HH:MM AM/PM"
              />
            </Field>
          )}
          {isAdd && (
            <Box
              sx={{
                p: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: TOKENS.amber200,
                background: TOKENS.amber50,
              }}
            >
              <Typography sx={{ fontSize: 11, color: TOKENS.amber900 }}>
                Photo capture is skipped — manager not present when bonus was
                given. The audit log records this as a late entry.
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderTop: `1px solid ${TOKENS.slate200}`,
            display: 'flex',
            gap: 1,
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            background: '#fff',
            pb: isPage ? 'calc(12px + env(safe-area-inset-bottom))' : 1.5,
          }}
        >
          <Button fullWidth variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={!canSave}
            onClick={handleSave}
            sx={{
              background: TOKENS.amber600,
              '&:hover': { background: TOKENS.amber700 },
            }}
          >
            {isAdd ? 'Add Bonus' : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </ResponsiveSheet>
  );
}

function Field({ label, children }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.slate500,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}
